import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AdminSchools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const { api } = useApi();

  const fetchSchools = useCallback(async () => {
    try {
      const response = await api.get('/admin/schools');
      setSchools(response.data.data.schools);
    } catch (error) {
      toast.error('Failed to load schools');
      console.error('Schools error:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleViewDetails = async (schoolId) => {
    try {
      const response = await api.get(`/admin/schools/${schoolId}`);
      setSelectedSchool(response.data.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load school details');
      console.error('School details error:', error);
    }
  };

  const handleExportSchools = () => {
    const csvContent = [
      ['School Name', 'Total Bands', 'Total Orders', 'Total Revenue', 'Active Bands', 'Completed Orders'],
      ...schools.map(school => [
        school.schoolName,
        school.totalBands,
        school.totalOrders,
        `$${school.totalRevenue.toFixed(2)}`,
        school.activeBands,
        school.completedOrders
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bandcam-schools-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Schools exported successfully');
  };

  const handleLogout = () => {
    localStorage.removeItem('bandcam_admin_token');
    delete api.defaults.headers.common['Authorization'];
    window.location.href = '/admin/login';
  };

  // Prepare chart data for school performance
  const schoolPerformanceData = schools.map(school => ({
    name: school.schoolName,
    orders: school.totalOrders,
    revenue: school.totalRevenue,
    bands: school.totalBands
  }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">BandCam Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportSchools}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Export Schools
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link
              to="/admin/dashboard"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Dashboard
            </Link>
            <Link
              to="/admin/bands"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Bands
            </Link>
            <Link
              to="/admin/orders"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Orders
            </Link>
            <Link
              to="/admin/schools"
              className="text-indigo-600 border-b-2 border-indigo-600 py-2 px-1 text-sm font-medium"
            >
              Schools
            </Link>
            <Link
              to="/admin/issues"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Issues
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* School Performance Chart */}
        {schools.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">School Performance Overview</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={schoolPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? `$${value.toFixed(2)}` : value,
                    name === 'revenue' ? 'Revenue' : name === 'orders' ? 'Orders' : 'Bands'
                  ]} />
                  <Bar dataKey="orders" fill="#3B82F6" name="Orders" />
                  <Bar dataKey="revenue" fill="#10B981" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Schools Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Schools ({schools.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Bands</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Bands</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schools.map((school) => (
                    <tr key={school.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {school.schoolName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {school.totalBands}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          school.activeBands > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {school.activeBands}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {school.totalOrders}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          school.completedOrders > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {school.completedOrders}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${school.totalRevenue.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(school.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* School Details Modal */}
      {showDetails && selectedSchool && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">School Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900">School Information</h4>
                  <p><span className="font-medium">Name:</span> {selectedSchool.school.schoolName}</p>
                  <p><span className="font-medium">Total Bands:</span> {selectedSchool.stats.totalBands}</p>
                  <p><span className="font-medium">Active Bands:</span> {selectedSchool.stats.activeBands}</p>
                  <p><span className="font-medium">Total Orders:</span> {selectedSchool.stats.totalOrders}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Performance</h4>
                  <p><span className="font-medium">Completed Orders:</span> {selectedSchool.stats.completedOrders}</p>
                  <p><span className="font-medium">Total Revenue:</span> ${selectedSchool.stats.totalRevenue.toFixed(2)}</p>
                  <p><span className="font-medium">Average Order Value:</span> ${selectedSchool.stats.averageOrderValue.toFixed(2)}</p>
                  <p><span className="font-medium">Completion Rate:</span> {selectedSchool.stats.completionRate.toFixed(1)}%</p>
                </div>
              </div>

              {selectedSchool.bands && selectedSchool.bands.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Bands at this School</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Band Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Director</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedSchool.bands.map((band) => (
                          <tr key={band.id}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{band.bandName}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{band.directorName}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                band.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {band.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{band.totalOrders || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSchools;
